import { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Upload, FilePdf, CheckCircle, HandCoins, CaretLeft, CaretRight } from '@phosphor-icons/react';
import { NC, STATUS_LABEL, orderFor } from './chequesApi';
import { DateField } from '@/components/ui/date-field';

export default function ChequeStatusActions({ cheque, onChanged }) {
  const [busy, setBusy] = useState(false);
  const [invoiceNumber, setInvoiceNumber] = useState('');
  const [invoiceDate, setInvoiceDate] = useState(new Date().toISOString().slice(0, 10));
  const [reembDate, setReembDate] = useState(new Date().toISOString().slice(0, 10));
  const [reembAmount, setReembAmount] = useState(String(cheque.net_expected ?? ''));
  const [paidAt, setPaidAt] = useState(new Date().toISOString().slice(0, 10));
  const [teamPaymentId, setTeamPaymentId] = useState('');

  const order = orderFor(cheque);
  const curIdx = order.indexOf(cheque.status);

  const advanceSimple = async (target) => {
    setBusy(true);
    try { await axios.put(`${NC}/${cheque.id}/status`, { status: target }); toast.success('Estado atualizado'); onChanged(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Erro'); }
    setBusy(false);
  };

  const goBack = async () => {
    if (curIdx <= 0) return;
    setBusy(true);
    try { await axios.put(`${NC}/${cheque.id}/status`, { status: order[curIdx - 1] }); toast.success('Estado revertido'); onChanged(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Erro'); }
    setBusy(false);
  };

  const uploadFatura = async (file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append('file', file); fd.append('invoice_number', invoiceNumber); fd.append('invoice_date', invoiceDate);
    setBusy(true);
    try { await axios.post(`${NC}/${cheque.id}/fatura`, fd, { headers: { 'Content-Type': 'multipart/form-data' } }); toast.success('Fatura carregada'); onChanged(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Erro ao carregar fatura'); }
    setBusy(false);
  };

  const registarReembolso = async () => {
    const amount = parseFloat(String(reembAmount).replace(',', '.'));
    if (!amount || amount <= 0) { toast.error('Valor inválido'); return; }
    setBusy(true);
    try { await axios.post(`${NC}/${cheque.id}/reembolso`, { reimbursement_date: reembDate, amount_received: amount }); toast.success('Reembolso registado'); onChanged(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Erro'); }
    setBusy(false);
  };

  const marcarPaga = async () => {
    setBusy(true);
    try { await axios.post(`${NC}/${cheque.id}/filipa-paga`, { paid_at: paidAt, team_payment_id: teamPaymentId }); toast.success('Marcado como pago'); onChanged(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Erro'); }
    setBusy(false);
  };

  return (
    <div className="space-y-4" data-testid="cheque-status-actions">
      <div className="flex items-center justify-between">
        <p className="text-xs font-medium text-brand-muted">Avançar estado</p>
        <button onClick={goBack} disabled={busy || curIdx <= 0} className="text-xs text-brand-muted disabled:opacity-30 flex items-center gap-1" data-testid="cheque-status-back">
          <CaretLeft size={12} /> Recuar um estado
        </button>
      </div>

      {cheque.status === 'pedido_recebido' && (
        <button onClick={() => advanceSimple('registado_validado')} disabled={busy} className="w-full bg-brand-text text-white rounded-full py-2 text-sm font-medium flex items-center justify-center gap-2" data-testid="cheque-advance-registado">
          <CaretRight size={14} /> Marcar como {STATUS_LABEL.registado_validado}
        </button>
      )}
      {cheque.status === 'registado_validado' && (
        <button onClick={() => advanceSimple('consulta_realizada')} disabled={busy || !cheque.consultation_id} className="w-full bg-brand-text text-white rounded-full py-2 text-sm font-medium flex items-center justify-center gap-2 disabled:opacity-40" data-testid="cheque-advance-consulta">
          <CaretRight size={14} /> Marcar como {STATUS_LABEL.consulta_realizada}
        </button>
      )}
      {cheque.status === 'registado_validado' && !cheque.consultation_id && (
        <p className="text-[11px] text-amber-600">Associa a consulta (campo acima) antes de avançar.</p>
      )}

      {cheque.status === 'consulta_realizada' && (
        <div className="bg-brand-bg/40 border border-brand-border rounded-xl p-3 space-y-2">
          <p className="text-xs font-medium text-brand-text flex items-center gap-1.5"><FilePdf size={14} /> Carregar fatura</p>
          <div className="grid grid-cols-2 gap-2">
            <input value={invoiceNumber} onChange={e => setInvoiceNumber(e.target.value)} placeholder="Número da fatura" className="rounded-lg border border-brand-border px-2 py-1.5 text-xs" data-testid="cheque-invoice-number" />
            <DateField value={invoiceDate} onChange={e => setInvoiceDate(e.target.value)} className="rounded-lg border border-brand-border px-2 py-1.5 text-xs" data-testid="cheque-invoice-date" />
          </div>
          <label className="w-full bg-brand-green text-white rounded-full py-2 text-sm font-medium flex items-center justify-center gap-2 cursor-pointer" data-testid="cheque-upload-fatura-label">
            <Upload size={14} /> Escolher PDF e carregar
            <input type="file" accept="application/pdf" className="hidden" onChange={e => uploadFatura(e.target.files?.[0])} data-testid="cheque-upload-fatura-input" />
          </label>
        </div>
      )}

      {cheque.status === 'fatura_emitida' && (
        <div className="bg-brand-bg/40 border border-brand-border rounded-xl p-3 space-y-2">
          <p className="text-xs font-medium text-brand-text">Registar reembolso recebido</p>
          <div className="grid grid-cols-2 gap-2">
            <DateField value={reembDate} onChange={e => setReembDate(e.target.value)} className="rounded-lg border border-brand-border px-2 py-1.5 text-xs" data-testid="cheque-reembolso-date" />
            <input inputMode="decimal" value={reembAmount} onChange={e => setReembAmount(e.target.value)} placeholder="Valor recebido €" className="rounded-lg border border-brand-border px-2 py-1.5 text-xs" data-testid="cheque-reembolso-amount" />
          </div>
          <button onClick={registarReembolso} disabled={busy} className="w-full bg-brand-green text-white rounded-full py-2 text-sm font-medium flex items-center justify-center gap-2" data-testid="cheque-registar-reembolso-btn">
            <CheckCircle size={14} /> Confirmar reembolso
          </button>
        </div>
      )}

      {cheque.status === 'reembolso_recebido' && cheque.requires_split && (
        <div className="bg-brand-bg/40 border border-brand-border rounded-xl p-3 space-y-2">
          <p className="text-xs font-medium text-brand-text flex items-center gap-1.5"><HandCoins size={14} /> Marcar {cheque.nutri_name || 'nutricionista'} como paga</p>
          <div className="grid grid-cols-2 gap-2">
            <DateField value={paidAt} onChange={e => setPaidAt(e.target.value)} className="rounded-lg border border-brand-border px-2 py-1.5 text-xs" data-testid="cheque-filipa-paid-date" />
            <input value={teamPaymentId} onChange={e => setTeamPaymentId(e.target.value)} placeholder="ID Pagamento Equipa (opcional)" className="rounded-lg border border-brand-border px-2 py-1.5 text-xs" data-testid="cheque-team-payment-id" />
          </div>
          <button onClick={marcarPaga} disabled={busy} className="w-full bg-emerald-600 text-white rounded-full py-2 text-sm font-medium" data-testid="cheque-marcar-paga-btn">Confirmar pagamento</button>
        </div>
      )}

      {(cheque.status === 'reembolso_recebido' && !cheque.requires_split) && (
        <p className="text-xs text-brand-muted text-center">Cheque concluído, sem divisão com nutricionista.</p>
      )}
      {cheque.status === 'filipa_paga' && (
        <p className="text-xs text-brand-green text-center font-medium">Ciclo completo, reembolso recebido e nutricionista paga.</p>
      )}
    </div>
  );
}
